/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useCustomerId, useCustomerBaskets} from '@salesforce/commerce-sdk-react'
import {isServer} from '@salesforce/retail-react-app/app/utils/utils'
import {useEffect, useRef} from 'react'
import {useGiftCardActions} from './use-gift-card-actions'

// Module-level variable to track if the useEffect is already running
// This is visible to all instances of the hook with the same value
let isRemovingGCPIs = false

/**
 * This hook combine some commerce-react-sdk hooks to provide more derived data for Retail App baskets
 * @param id - basket id to get the current used basket among baskets returned, use first basket in the array if not defined
 * @param shouldFetchProductDetail - boolean to indicate if the baskets should fetch product details based on basket items
 */
export const useCurrentBasket = ({id = ''} = {}) => {
    const customerId = useCustomerId()

    const {data: basketsData, ...restOfQuery} = useCustomerBaskets(
        {parameters: {customerId}},
        {
            enabled: !!customerId && !isServer
        }
    )

    const currentBasket =
        basketsData?.baskets?.find((basket) => basket?.basketId === id) || basketsData?.baskets?.[0]

    const {removeAllGiftCardPaymentInstruments} = useGiftCardActions(currentBasket)

    // Sum the product items quantities excluding gifts not to be sent
    let numberOfProductItems =
        currentBasket?.productItems
            ?.filter((productItem) => {
                const isGift = productItem.c_sendThisGift === true
                const isBonus = productItem.bonusProductLineItem === true
                return !isGift && !isBonus
            })
            ?.reduce((acc, item) => acc + item.quantity, 0) || 0

    const giftCertificateLineItems = currentBasket?.giftCertificateItems ?? []
    const giftCertificateLineItemCount = giftCertificateLineItems.length
    const isBuyingOnlyGiftCards = numberOfProductItems === 0 && giftCertificateLineItemCount > 0

    // This logic removes all gift card payment instruments when the order total changes
    const prevOrderTotal = useRef(currentBasket?.orderTotal)
    useEffect(() => {
        removeGCPIs()

        async function removeGCPIs() {
            const currentTotal = currentBasket?.orderTotal

            if (
                !isRemovingGCPIs &&
                typeof currentTotal === 'number' &&
                typeof prevOrderTotal.current === 'number' &&
                currentTotal !== prevOrderTotal.current
            ) {
                isRemovingGCPIs = true
                await removeAllGiftCardPaymentInstruments()
                isRemovingGCPIs = false
            }

            prevOrderTotal.current = currentTotal
        }
    }, [currentBasket?.orderTotal])

    return {
        ...restOfQuery,
        data: currentBasket,
        derivedData: {
            hasBasket: basketsData?.total > 0,
            totalItems: numberOfProductItems + giftCertificateLineItemCount,
            isBuyingOnlyGiftCards,
            giftCertificateLineItemCount,
            giftCertificateLineItemCost: giftCertificateLineItems.reduce(
                (acc, {amount}) => acc + amount,
                0
            ),
            bonusProductLineItemsCount:
                currentBasket?.productItems
                    ?.filter(
                        (productItem) =>
                            productItem.bonusProductLineItem === true && productItem.c_sendThisGift
                    )
                    ?.reduce((acc, item) => acc + item.quantity, 0) || 0
        }
    }
}
